import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import request from '../utils/request';
import {
  extractSignContractErrorCode,
  resolveSignContractErrorMessage
} from '../utils/signContractErrors';
import EN from '../../EN.json';

const DUPLICATE_SUBMIT_MESSAGE =
  (EN.contractSignature && EN.contractSignature.duplicateSubmitMessage) ||
  'This contract is already being submitted.';

// Cross-tab lock for the FINAL submit only. TTL-bounded so a crashed/closed
// tab can never permanently block a legitimate retry.
const FINAL_LOCK_TTL_MS = 60000;
const finalLockKey = token => `sign-contract-final-${token}`;

const isFinalLocked = token => {
  try {
    const raw = localStorage.getItem(finalLockKey(token));
    if (!raw) return false;
    const ts = Number(raw);
    return Boolean(ts) && Date.now() - ts < FINAL_LOCK_TTL_MS;
  } catch (e) {
    return false;
  }
};

const setFinalLock = token => {
  try {
    localStorage.setItem(finalLockKey(token), String(Date.now()));
  } catch (e) {
    // localStorage unavailable — same-tab inFlightRef + backend guard still apply.
  }
};

const clearFinalLock = token => {
  try {
    localStorage.removeItem(finalLockKey(token));
  } catch (e) {
    // no-op
  }
};

/**
 * Shared signing-submit logic for every SignContract variant.
 *
 * Owns duplicate-submit protection (T052), backend error-code mapping (T053),
 * and the two-phase preview/final flow (T055). Preview posts
 * `isStatusChanged:false`; final posts `isStatusChanged:true` — the caller
 * decides which via the `isStatusChanged` argument, so preview can never
 * finalize.
 *
 * @param {Object} params
 * @param {string} params.token
 * @param {string} params.expiredPath  - legacy fallback route (unknown/absent code)
 * @param {string} params.successPath  - route after a confirmed final submit
 * @param {boolean} [params.persistTokenOnSuccess=false] - FM stores the token
 */
const useSignContractSubmit = ({
  token,
  expiredPath,
  successPath,
  persistTokenOnSuccess = false
}) => {
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const inFlightRef = useRef(false);

  const clearSubmitError = () => setSubmitError(null);

  const handleFailure = ({ data, error }) => {
    const code = extractSignContractErrorCode({ data, error });
    const message = resolveSignContractErrorMessage(code);
    if (message) {
      setSubmitError(message);
    } else {
      // Legacy backend / no code / network error → preserve existing behavior.
      history.push(expiredPath);
    }
  };

  const submit = async ({ signature, isStatusChanged, afterPreview }) => {
    if (inFlightRef.current) return; // guard: blocks double-click within this tab
    if (isStatusChanged && isFinalLocked(token)) {
      // Another tab is already finalizing this same signing link.
      setSubmitError(DUPLICATE_SUBMIT_MESSAGE);
      return;
    }
    inFlightRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    if (isStatusChanged) setFinalLock(token);

    try {
      const res = await request.post('/orders/contract-signed', {
        token,
        signature,
        isStatusChanged
      });
      const data = (res && res.data) || {};

      if (data.success) {
        setShowPreview(true);
        if (typeof afterPreview === 'function') afterPreview();
        if (isStatusChanged) {
          if (persistTokenOnSuccess) {
            localStorage.setItem('signContractToken', token);
          }
          history.push(successPath);
        }
        return;
      }

      if (isStatusChanged) clearFinalLock(token);
      handleFailure({ data });
    } catch (error) {
      if (isStatusChanged) clearFinalLock(token);
      handleFailure({ error });
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  return {
    submitting,
    showPreview,
    setShowPreview,
    submitError,
    clearSubmitError,
    submit
  };
};

export default useSignContractSubmit;
